import { useLayoutEffect, useRef, useState } from 'react';
import { twoLines } from '../../lib/textLines';

let measureCanvas: HTMLCanvasElement | null = null;

/** The width of a text at a size, in the font of an element. */
function textWidth(el: HTMLElement, text: string, size: number): number {
  measureCanvas ??= document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return 0;
  const cs = window.getComputedStyle(el);
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${size}px ${cs.fontFamily}`;
  return ctx.measureText(cs.textTransform === 'uppercase' ? text.toUpperCase() : text).width;
}

/** A text with the largest size from `min` to `max` that fits its box (#608).
 *  The page measures the text in the font of the screen: a size from the
 *  count of letters clipped long Lithuanian last names ("BAGDONA"). With
 *  `wrap`, a text that does not fit at `min` breaks into two lines at a
 *  hyphen or a space.
 *
 *  Two lines take `min` or a smaller size, so a parent
 *  can give the name a row of one height for each name (operator,
 *  2026-10-15). A text that still does not fit takes a smaller size, down
 *  to `floor` (default `min`). If it does not fit at `floor`, a line ends
 *  with "…". The box is the width of the parent, so the parent sets the
 *  room. */
export default function FitText({ text, max, min, floor = min, wrap = false, className = '', testId }: {
  text: string; max: number; min: number; floor?: number; wrap?: boolean; className?: string; testId?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  // The fit names its text: a fit of an older text does not apply to a new one.
  const [fit, setFit] = useState<{ text: string; size: number; lines: [string, string] | null }>({ text, size: max, lines: null });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let live = true;
    const sizeFor = (width: number, room: number, top = max) => Math.max(floor, Math.min(top, Math.floor((max * room / width) * 0.97)));
    const measure = () => {
      if (!live) return;
      const room = el.clientWidth;
      const width = textWidth(el, text, max);
      if (!(width > 0 && room > 0)) return;
      const one = Math.floor((max * room / width) * 0.97);
      const parts = wrap && one < min ? twoLines(text) : null;
      if (!parts) { setFit({ text, size: sizeFor(width, room), lines: null }); return; }
      const longest = Math.max(textWidth(el, parts[0], max), textWidth(el, parts[1], max));
      setFit({ text, size: sizeFor(longest, room, min), lines: parts });
    };
    measure();
    // A web font can come after the first measure, with other widths.
    void document.fonts?.ready.then(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => { live = false; observer.disconnect(); };
  }, [text, max, min, floor, wrap]);
  const own = fit.text === text ? fit : { size: max, lines: null };
  // The text of the page stays the name: a break at a space keeps its space,
  // a break after a hyphen adds nothing.
  const gap = own.lines && own.lines[0] + own.lines[1] !== text.trim() ? ' ' : '';
  // w-full: the span takes the width of its parent also in a centred column.
  // There, a span of the width of its text measured itself, and each pass
  // made the text smaller, down to `min` (logic review of #608).
  // `room`: the clip box of a line gets space above and below. The caron or
  // the dot of a capital (Ž, Č, Š, Ė) goes above the line, and `truncate`
  // cut it. The negative margin keeps the layout. Two lines sit in a flex
  // column, where the margins of the two lines do not collapse.
  const room = 'py-[0.2em] -my-[0.2em]';
  return (
    <span ref={ref} data-testid={testId} data-lines={own.lines ? 2 : 1}
      className={`w-full ${own.lines ? 'flex flex-col' : `block truncate ${room}`} ${className}`} style={{ fontSize: own.size }}>
      {own.lines
        ? <><span className={`block truncate ${room}`}>{own.lines[0]}</span>{gap}<span className={`block truncate ${room}`}>{own.lines[1]}</span></>
        : text}
    </span>
  );
}
